"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Zoom,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from "@mui/material";
import {
  ContentCopy,
  Brightness4,
  Brightness7,
  ExpandMore,
  ColorLens,
  Message,
  Settings,
  Code,
  CheckCircle,
} from "@mui/icons-material";
import { useTheme } from '../style/theme';
import { useMessage } from "../components/Header";

export default function EmbededCode() {
  const { mode, toggleTheme } = useTheme();
  const { showSuccess } = useMessage();
  const [baseUrl, setBaseUrl] = useState("");

  // Widget settings
  const [widgetSettings, setWidgetSettings] = useState({
    position: "bottom-right",
    greetingMessage: "Hello! How can we help you?",
    headerColor: "#007bff",
    userMessageColor: "#007bff",
    userMessageTextColor: "#000000",
    agentMessageColor: "#eee",
    agentMessageTextColor: "#222",
    widgetBackgroundColor: "#fff",
    fontFamily: "sans-serif",
    requirePreChatForm: false,
    isOffline: false,
    autoRespondOffline: true,
    offlineAutoResponseMessage:
      "Thanks for your message! We'll get back to you soon.",
    chatIconUrl: "https://cdn.clikkle.com/images/chat/logo/2025/clikkle%20chat.png",
    startConversationButtonText: "Start a Conversation",
      organizationLogo: "${process.env.REACT_APP_BACKEND_URL}/${organizationLogo || ''}"
  });

  // Generate widget code based on current settings
  const org = JSON.parse(localStorage.getItem("org"));
  const organizationId = org?._id;
  const organizationLogo = org?.logo || '';
  console.log("EmbededCode: Organization data:", { organizationId, organizationLogo, org });

  const userId = JSON.parse(localStorage.getItem("user"))?._id;

  console.log(userId, "userId");

  const generateWidgetCode = () => {
    return `
    <script>
      window.$mychat = window.$mychat || {};
      $mychat.config = {
        organizationId: "${organizationId}",
        widgetId: "${userId}",
        socketUrl: "${process.env.REACT_APP_SOCKET_URL}",
        apiUrl: "${process.env.REACT_APP_BACKEND_URL}",
        name: "Support Chat",
        position: "${widgetSettings.position}",
        greetingMessage: "${widgetSettings.greetingMessage}",
        headerColor: "${widgetSettings.headerColor}",
        userMessageColor: "${widgetSettings.userMessageColor}",
        userMessageTextColor: "${widgetSettings.userMessageTextColor}",
        agentMessageColor: "${widgetSettings.agentMessageColor}",
        agentMessageTextColor: "${widgetSettings.agentMessageTextColor}",
        widgetBackgroundColor: "${widgetSettings.widgetBackgroundColor}",
        fontFamily: "${widgetSettings.fontFamily}",
        requirePreChatForm: ${widgetSettings.requirePreChatForm},
        isOffline: ${widgetSettings.isOffline},
        autoRespondOffline: ${widgetSettings.autoRespondOffline},
        offlineAutoResponseMessage: "${widgetSettings.offlineAutoResponseMessage}",
        chatIconUrl: "${widgetSettings.chatIconUrl}",
        startConversationButtonText: "${widgetSettings.startConversationButtonText}",
        organizationLogo: "${process.env.REACT_APP_BACKEND_URL}/${organizationLogo}"
      };
    </script>
    <script src="${baseUrl}/chat-widget.iife.js"></script>`;
  };

  // Handle settings change
  const handleSettingChange = (setting, value) => {
    setWidgetSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  // Copy widget code to clipboard
  const copyWidgetCode = () => {
    navigator.clipboard.writeText(generateWidgetCode());
    showSuccess("Code Copied! Please add this to the main HTML file of your project.");
  };

  // Check system preference for theme on initial load
  useEffect(() => {
    const prefersDarkMode = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    setBaseUrl(window.location.origin);
  }, []);

  return (
    <Box sx={{
      minHeight: "100vh",
      p: 3,
      bgcolor: mode === "dark" ? "#121212" : "#f5f5f5",
    }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" fontWeight="bold" color="primary">
            Chat Widget Configuration
          </Typography>
          
        </Box>

        <Grid container spacing={4}>
          {/* Settings Panel */}
          <Grid item xs={12} md={7}>
            <div>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  height: "100%",
                  bgcolor: mode === "dark" ? "#1e1e1e" : "#ffffff",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Settings fontSize="small" /> Widget Settings
                </Typography>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Message fontSize="small" /> Basic Settings
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          label="Widget Position"
                          value={widgetSettings.position}
                          onChange={(e) =>
                            handleSettingChange("position", e.target.value)
                          }
                          margin="normal"
                          size="small"
                        >
                          <MenuItem value="bottom-right">
                            Bottom Right
                          </MenuItem>
                          <MenuItem value="bottom-left">Bottom Left</MenuItem>
                          <MenuItem value="top-right">Top Right</MenuItem>
                          <MenuItem value="top-left">Top Left</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Start Conversation Button Text"
                          value={widgetSettings.startConversationButtonText}
                          onChange={(e) =>
                            handleSettingChange("startConversationButtonText", e.target.value)
                          }
                          margin="normal"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Greeting Message"
                          value={widgetSettings.greetingMessage}
                          onChange={(e) =>
                            handleSettingChange("greetingMessage", e.target.value)
                          }
                          margin="normal"
                          size="small"
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Font Family"
                          value={widgetSettings.fontFamily}
                          onChange={(e) =>
                            handleSettingChange("fontFamily", e.target.value)
                          }
                          margin="normal"
                          size="small"
                        >
                          <MenuItem value="sans-serif">Sans-serif</MenuItem>
                          <MenuItem value="Arial, sans-serif">Arial</MenuItem>
                          <MenuItem value="Roboto, sans-serif">
                            Roboto
                          </MenuItem>
                          <MenuItem value="'Open Sans', sans-serif">
                            Open Sans
                          </MenuItem>
                          <MenuItem value="'Segoe UI', sans-serif">
                            Segoe UI
                          </MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <ColorLens fontSize="small" /> Appearance
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Header Color"
                          value={widgetSettings.headerColor}
                          onChange={(e) =>
                            handleSettingChange("headerColor", e.target.value)
                          }
                          margin="normal"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: widgetSettings.headerColor,
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="User Message Color"
                          value={widgetSettings.userMessageColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "userMessageColor",
                              e.target.value
                            )
                          }
                          margin="normal"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: widgetSettings.userMessageColor,
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="User Message Text Color"
                          value={widgetSettings.userMessageTextColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "userMessageTextColor",
                              e.target.value
                            )
                          }
                          margin="normal"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor:
                                      widgetSettings.userMessageTextColor,
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Agent Message Color"
                          value={widgetSettings.agentMessageColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "agentMessageColor",
                              e.target.value
                            )
                          }
                          margin="normal"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: widgetSettings.agentMessageColor,
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Agent Message Text Color"
                          value={widgetSettings.agentMessageTextColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "agentMessageTextColor",
                              e.target.value
                            )
                          }
                          margin="normal"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor:
                                      widgetSettings.agentMessageTextColor,
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Widget Background Color"
                          value={widgetSettings.widgetBackgroundColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "widgetBackgroundColor",
                              e.target.value
                            )
                          }
                          margin="normal"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor:
                                      widgetSettings.widgetBackgroundColor,
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Settings fontSize="small" /> Advanced Settings
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={widgetSettings.requirePreChatForm}
                              onChange={(e) =>
                                handleSettingChange(
                                  "requirePreChatForm",
                                  e.target.checked
                                )
                              }
                              color="primary"
                            />
                          }
                          label="Require Pre-Chat Form"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={widgetSettings.isOffline}
                              onChange={(e) =>
                                handleSettingChange("isOffline", e.target.checked)
                              }
                              color="primary"
                            />
                          }
                          label="Offline Mode"
                        />
                      </Grid>
                      {widgetSettings.isOffline && (
                        <>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={widgetSettings.autoRespondOffline}
                                  onChange={(e) =>
                                    handleSettingChange(
                                      "autoRespondOffline",
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label="Auto-Respond When Offline"
                            />
                          </Grid>
                          {widgetSettings.autoRespondOffline && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Offline Auto-Response Message"
                                value={
                                  widgetSettings.offlineAutoResponseMessage
                                }
                                onChange={(e) =>
                                  handleSettingChange(
                                    "offlineAutoResponseMessage",
                                    e.target.value
                                  )
                                }
                                margin="normal"
                                size="small"
                                multiline
                                rows={2}
                              />
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </div>
          </Grid>

          {/* Code Preview */}
          <Grid item xs={12} md={5}>
            <div>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  height: "100%",
                  bgcolor:
                    mode === "dark" ? "#1a1a1a" : "#f8f9fa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Code fontSize="small" /> Widget Code
                  </Typography>
                  <Tooltip
                    title="Copy to clipboard"
                    TransitionComponent={Zoom}
                  >
                    <IconButton onClick={copyWidgetCode} color="primary">
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor:
                      mode === "dark" ? "#121212" : "#f1f3f5",
                    border: "1px solid",
                    borderColor:
                      mode === "dark" ? "#333" : "#ddd",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    overflow: "auto",
                    maxHeight: "500px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {generateWidgetCode()}
                </Box>
                <Box
                  sx={{ mt: 3, display: "flex", justifyContent: "center" }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ContentCopy />}
                    onClick={copyWidgetCode}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                    }}
                  >
                    Copy Widget Code
                  </Button>
                </Box>
              </Paper>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
